<template>
  <Portal>
    <Modal
      class="portfolio-terms-modal"
      :is-open="isOpen"
      @close="$emit('close')"
    >
      <div v-if="portfolioTerms?.requestRisksTerm">
        <Heading :level="3">{{ portfolioTerms.title }}</Heading>
        <BaseText size="p4" class="portfolio-terms-modal__description">
          {{ portfolioTerms.description }}
        </BaseText>
        <ContentDisplay
          class="portfolio-terms-modal__content"
          footer-label="Visualizar termo"
          footer-type="link"
          @click="goToTerms(portfolioTerms.termUrl)"
        >
          <template #content-block-1>
            <ListDisplay
              v-for="(detail, index) in portfolioTerms.riskDetails"
              :key="index"
              class="portfolio-terms-modal__item"
              :title="detail.label"
              :value="detail.value"
              :has-divider="index !== portfolioTerms.riskDetails.length - 1"
            />
          </template>
        </ContentDisplay>
        <BaseText size="p4" class="portfolio-terms-modal__content">
          {{ portfolioTerms.footer }}
        </BaseText>
      </div>
      <template #buttonContainer>
        <ButtonContainer>
          <template #button-primary>
            <ButtonPrimary
              size="large"
              label="Aceitar"
              :loading="isLoading"
              @click="termsSignature(portfolioTerms)"
            />
          </template>
          <template #button-secondary>
            <ButtonSecondary
              size="large"
              label="Cancelar"
              @click="$emit('close')"
            />
          </template>
        </ButtonContainer>
      </template>
    </Modal>
  </Portal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  Modal,
  Heading,
  ContentDisplay,
  BaseText,
  ListDisplay,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { Portal } from '@/modules/common/components/Portal';

import { portfolioTermSignature } from '@/modules/wealth/services/portfolio-terms';
import { IPortfolioTermsData } from './types';

interface Props {
  isOpen: boolean;
  portfolioId: string;
  portfolioTerms: IPortfolioTermsData;
}

interface EmitEvents {
  (event: 'close'): void;
  (event: 'accepted'): void;
}

const { portfolioId, portfolioTerms } = defineProps<Props>();

const emit = defineEmits<EmitEvents>();

const isLoading = ref(false);

const portfolioIdParam = computed(() => {
  return portfolioId;
});

function goToTerms(url: string) {
  window.open(url, '_blank');
}

async function termsSignature(data: IPortfolioTermsData) {
  try {
    isLoading.value = true;

    const params = {
      termId: data.termId!,
      customerRiskLabel: data.customerRiskLabel!,
      recommendedPortfolioRisk: data.recommendedPortfolioRisk!,
      portfolioRiskScore: data.portfolioRiskScore!,
      portfolioRiskLabel: data.portfolioRiskLabel!
    };

    await portfolioTermSignature(portfolioIdParam.value, params);

    emit('accepted');
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível aceitar os termos. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });

    emit('close');
  } finally {
    isLoading.value = false;
  }
}
</script>

<style lang="less" scoped>
.portfolio-terms-modal {
  :deep(.base-modal--wrapper) {
    max-width: 592px;
  }

  :deep(.modal-content) {
    align-items: flex-start;
    text-align: left;
    padding: 0 @size-spacing-x1000 @size-spacing-x1000;
  }

  :deep(.content-display) {
    background-color: @background-tertiary;
    border: none;
  }

  :deep(.footer-button__inner) {
    background-color: @background-tertiary;

    p {
      color: @element-primary !important;
    }
  }

  &__description {
    margin-top: @size-spacing-x600;
  }

  &__content {
    margin-top: @size-spacing-x500;
  }

  &__item {
    margin-bottom: @size-spacing-x350;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
}
</style>
